import { useState } from 'react';
import { decks } from '@/lib/api';
import { toast } from '@/lib/toast';

interface VisibilityToggleProps {
  deckId: string;
  currentVisibility: string;
  onChanged?: (newVis: string) => void;
}

export function VisibilityToggle({ deckId, currentVisibility, onChanged }: VisibilityToggleProps) {
  const [visibility, setVisibility] = useState(currentVisibility);
  const [updating, setUpdating] = useState(false);

  const isPublic = visibility === 'public';

  async function handleToggle() {
    if (updating) return;

    const newVis = isPublic ? 'private' : 'public';
    const prevVis = visibility;

    // Optimistic update
    setVisibility(newVis);
    setUpdating(true);

    try {
      await decks.updateVisibility(deckId, newVis as 'public' | 'private');
      toast.success(`Deck is now ${newVis}`);
      onChanged?.(newVis);
    } catch (e) {
      // Rollback
      setVisibility(prevVis);
      toast.error(e instanceof Error ? e.message : 'Failed to update visibility');
    } finally {
      setUpdating(false);
    }
  }

  return (
    <button
      onClick={handleToggle}
      disabled={updating}
      title={isPublic ? 'Make private' : 'Make public'}
      className="inline-flex items-center gap-1.5 py-[5px] px-3.5 rounded-full border border-outline-variant text-base font-medium whitespace-nowrap transition-[background,color,opacity] duration-150"
      style={{
        background: isPublic
          ? 'rgba(var(--primary-rgb, 187,134,252), 0.12)'
          : 'var(--surface-container-high)',
        color: isPublic ? 'var(--primary)' : 'var(--on-surface-variant)',
        cursor: updating ? 'not-allowed' : 'pointer',
        opacity: updating ? 0.6 : 1,
      }}
    >
      <span className="text-[15px]" aria-hidden="true">
        {isPublic ? '\uD83D\uDC41' : '\uD83D\uDD12'}
      </span>
      {isPublic ? 'Public' : 'Private'}
    </button>
  );
}
